import * as React from 'react';
import { connect } from 'react-redux';
import { Card } from '@origin-digital/layout';
import { Formik, FormikProps, FormikValues } from 'formik';

import { Fields } from './Fields';
import { validation } from './validation';

import { RequestServiceProps, RequestServiceState } from './RequestService.types';

import ThankYou from '../../components/ThankYou';
import Banner from '../../components/Banner';
import { ResponsiveLayout } from '../../components/ResponsiveLayout';
import FormErrorSummary from '../../components/Form/Layout/FormErrorSummary';

import { submitCreateEnquiry } from '../../redux/thunks';
import { resetCustomerDetail } from '../../redux/actions/customer';
import { isInitialValid } from '../../utils/formHelpers';
import logger from '../../logging';
import analytics from '../../analytics';
import { CreateEnquiryInput } from '../../../__generated__/globalTypes';

import general from './content.json';

export class RequestService extends React.Component<RequestServiceProps, RequestServiceState> {
  state = {
    showThankYouPage: false
  };

  componentDidMount() {
    analytics.pageLoad('form');
  }

  async submit(values: FormikValues) {
    const {
      submitCreateEnquiry,
      enquiryType,
      postcode,
      state,
      allowMarketingCommunications
    } = this.props;

    try {
      const { data } = await submitCreateEnquiry(this.mapFormValuesToCreateEnquiryInput(values));

      if (data) {
        analytics.submitSuccessful({
          enquiryType: enquiryType,
          postcode: postcode,
          state: state,
          allowMarketingCommunications: allowMarketingCommunications
        });

        this.showThankYouPage();
      }
    } catch (error) {
      logger.error('GraphQL Error', error);
      return;
    }
  }

  mapFormValuesToCreateEnquiryInput(values: FormikValues): CreateEnquiryInput {
    return {
      enquiryType: values.enquiryType,
      comment: values.comment,
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phone: values.phone,
      address: {
        unit: values.unit,
        streetNumber: values.streetNumber,
        streetName: values.streetName,
        streetType: values.streetType,
        suburb: values.suburb,
        state: values.state,
        postcode: values.postcode
      },
      allowMarketingCommunications: !values.allowMarketingCommunications
    };
  }

  showThankYouPage(): void {
    this.setState({
      showThankYouPage: true
    });
  }

  render(): JSX.Element | null {
    const { initialValues } = this.props;
    const { showThankYouPage } = this.state;

    return (
      <React.Fragment>
        <Banner
          title={'Solar repairs, cleaning and service'}
          subtitle={
            "Please use the form below for any solar maintenance needs. We'll look after you for the life of your system."
          }
        />
        <ResponsiveLayout min={['SM', 'MD']}>
          <Card>
            <FormErrorSummary marginBottom={8} />
            {showThankYouPage ? (
              <ThankYou data={general} />
            ) : (
              <Formik
                initialValues={initialValues}
                validationSchema={validation}
                enableReinitialize={false}
                isInitialValid={isInitialValid}
                onSubmit={this.submit.bind(this)}
                render={({ isValid }: FormikProps<FormikValues>) => (
                  <Fields isInitialValid={isInitialValid} isValid={isValid} />
                )}
              />
            )}
          </Card>
        </ResponsiveLayout>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: any) => ({
  initialValues: state.customer,
  postcode: state.customer.postcode,
  state: state.customer.state,
  enquiryType: state.customer.enquiryType,
  allowMarketingCommunications: state.customer.allowMarketingCommunications
    ? state.customer.allowMarketingCommunications
    : false
});

export default connect(
  mapStateToProps,
  { submitCreateEnquiry, resetCustomerDetail }
)(RequestService);
